import { Link, useLocation } from 'react-router-dom';
import { Home, ArrowLeft } from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { PageTitle, PageDescription } from '@reflex/ui/components/layout/Typography';
import { Button } from '@reflex/ui/components/ui/button';
import { ORG_PATH_PREFIX, ROUTES } from '@/lib/routes';
import { useAuth } from '@/hooks/auth/useAuth';

/**
 * Home link target the signed-in user can actually open. The URL's
 * `:orgSlug` may point to an org the user is not a member of (the
 * `OrgSlugResolver` 404 case); routing Home to it loops back into the
 * same inaccessible page (REF-140). `OrgSlugResolver` keeps
 * `activeMembership` on a real org for both the matched and unmatched
 * cases, so read from the session rather than the URL.
 */
function useAccessibleHomeHref(): string {
  const { activeMembership } = useAuth();
  const slug = activeMembership?.organization.slug ?? null;
  return slug ? ROUTES.org(slug).home : `/${ORG_PATH_PREFIX}`;
}

export function NotFoundPage() {
  const { pathname } = useLocation();
  const homeHref = useAccessibleHomeHref();

  return (
    <PageLayout>
      <div className="flex flex-1 flex-col items-center justify-center gap-6 text-center">
        <div className="space-y-2">
          <p className="text-7xl font-bold tracking-tighter text-muted-foreground">404</p>
          <PageTitle className="justify-center text-xl">Page not found</PageTitle>
          <PageDescription className="mt-0">
            No route matches{' '}
            <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{pathname}</code>
          </PageDescription>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" size="sm" asChild>
            <Link to={homeHref}>
              <Home className="size-4" />
              Home
            </Link>
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.history.back()}>
            <ArrowLeft className="size-4" />
            Go back
          </Button>
        </div>
      </div>
    </PageLayout>
  );
}
